import React, { useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { auth, db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { avatarUrl, AVATAR_VARIANTS, staffAvatar } from '../lib/avatar';
import { doc, getDoc, setDoc, updateDoc, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useSchoolId } from '../lib/auth/schoolContext';
import { useUserRole, ROLES } from '../lib/auth/roles';

export default function StaffProfile() {
  const user = auth.currentUser;
  const { schoolId } = useSchoolId();
  const { role } = useUserRole();
  const [userDoc, setUserDoc] = useState(null);
  const [staffDoc, setStaffDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', photoUrl: '', avatarUseGenerated: false, avatarVariant: 'adventurer', avatarSeed: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      if (!user) { setLoading(false); return; }
      try {
        const uref = doc(db, 'users', user.uid);
        const usnap = await getDoc(uref);
        const u = usnap.exists() ? { id: usnap.id, ...usnap.data() } : null;
        setUserDoc(u);
        if (u?.staffId) {
          const sref = doc(db, 'staff', u.staffId);
          const ssnap = await getDoc(sref);
          if (ssnap.exists()) {
            const s = { id: ssnap.id, ...ssnap.data() };
            setStaffDoc(s);
            setForm({
              name: s.name || '',
              email: s.email || user.email || '',
              photoUrl: s.photoUrl || '',
              avatarUseGenerated: s.avatarUseGenerated === true,
              avatarVariant: 'adventurer',
              avatarSeed: s.avatarSeed || s.profileSelectedSeed || s.email || s.name || user.uid
            });
          }
        } else {
          setForm({ name: user.displayName || '', email: user.email || '', photoUrl: '', avatarUseGenerated: false, avatarVariant: 'adventurer', avatarSeed: user.email || user.displayName || user.uid });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const linkOrCreateStaff = async () => {
    if (!user) return;
    setSaving(true);
    try {
      // Try to find existing staff by email
      if (!userDoc?.staffId && form.email) {
        const qSnap = await getDocs(query(collection(db, 'staff'), where('schoolId','==', schoolId), where('email','==', form.email)));
        if (!qSnap.empty) {
          const found = { id: qSnap.docs[0].id, ...qSnap.docs[0].data() };
          await updateDoc(doc(db, 'users', user.uid), { staffId: found.id });
          setUserDoc(prev => ({ ...(prev||{}), staffId: found.id }));
          setStaffDoc(found);
          return found;
        }
      }
      // Create new staff doc
      const payload = {
        name: form.name || user.displayName || 'Unnamed',
        email: form.email || user.email || '',
        role: role || ROLES.TEACHER,
        active: true,
        photoUrl: form.photoUrl || '',
        avatarUseGenerated: !!form.avatarUseGenerated,
        avatarVariant: 'adventurer',
        avatarSeed: form.avatarSeed || form.email || form.name || user.uid,
        schoolId,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const ref = await addDoc(collection(db, 'staff'), payload);
      await updateDoc(doc(db, 'users', user.uid), { staffId: ref.id });
      const staffNew = { id: ref.id, ...payload };
      setStaffDoc(staffNew);
      setUserDoc(prev => ({ ...(prev||{}), staffId: ref.id }));
      return staffNew;
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      let currentStaff = staffDoc;
      if (!currentStaff) {
        currentStaff = await linkOrCreateStaff();
      }
      if (!currentStaff) return;
      const updates = {
        name: form.name,
        email: form.email,
        photoUrl: form.photoUrl,
        avatarUseGenerated: !!form.avatarUseGenerated,
        avatarVariant: 'adventurer',
        avatarSeed: form.avatarSeed || form.email || form.name || user.uid,
        updatedAt: new Date(),
        schoolId,
      };
      await updateDoc(doc(db, 'staff', currentStaff.id), updates);
      setStaffDoc(prev => ({ ...(prev||{}), ...updates }));
      // also mirror preferred name/email in user doc for convenience
      await setDoc(doc(db, 'users', user.uid), { preferredName: form.name, email: form.email, updatedAt: new Date() }, { merge: true });
      alert('Profile updated');
    } catch (e) {
      console.error('Failed to save profile', e);
      alert('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const onFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !user) return;
    try {
      setUploading(true);
      // Read image into canvas, center-crop to square and resize to 512x512
      const bitmap = await createImageBitmap(file);
      const minSide = Math.min(bitmap.width, bitmap.height);
      const sx = Math.floor((bitmap.width - minSide) / 2);
      const sy = Math.floor((bitmap.height - minSide) / 2);
      const sSize = minSide;
      const targetSize = 512; // final avatar size
      const canvas = document.createElement('canvas');
      canvas.width = targetSize;
      canvas.height = targetSize;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, sx, sy, sSize, sSize, 0, 0, targetSize, targetSize);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.9);
      });

      const path = `staff/${schoolId || 'default'}/${user.uid}-avatar-512.jpg`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      setForm((f) => ({ ...f, photoUrl: url }));
    } catch (e) {
      console.error('Photo upload failed', e);
      alert('Failed to upload photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const [leadInterventions, setLeadInterventions] = useState([]);
  useEffect(() => {
    (async () => {
      if (!staffDoc?.id) { setLeadInterventions([]); return; }
      const snap = await getDocs(query(collection(db, 'interventions'), where('schoolId','==', schoolId), where('leadStaffId','==', staffDoc.id)));
      setLeadInterventions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, [staffDoc?.id, schoolId]);

  if (loading) return <AppLayout><div className="p-6 text-gray-600">Loading…</div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-sm text-gray-600">Manage your contact details and staff profile.</p>
        </div>

        <div className="bg-white rounded shadow p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 flex flex-col items-center">
            <img alt="" src={staffAvatar({ id: user?.uid, email: form.email, name: form.name, photoUrl: form.photoUrl, avatarUseGenerated: form.avatarUseGenerated, avatarVariant: form.avatarVariant }, 128)} className="h-24 w-24 rounded-full object-cover" />
            <div className="mt-3 flex flex-col items-center gap-2">
              <label className="inline-flex items-center px-3 py-1.5 border rounded text-sm cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={onFileSelected} />
                {uploading ? 'Uploading…' : 'Upload photo'}
              </label>
              <div className="text-xs text-gray-500">or paste a public image URL below</div>
            </div>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Name</label>
              <input className="w-full border rounded p-2" value={form.name} onChange={(e)=>setForm(f=>({...f,name:e.target.value}))} />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Email</label>
              <input className="w-full border rounded p-2" value={form.email} onChange={(e)=>setForm(f=>({...f,email:e.target.value}))} />
            </div>
            <div className="md:col-span-2">
            {form.avatarUseGenerated && (
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-1">Choose your avatar</label>
                <div className="grid grid-cols-6 gap-3">
                  {Array.from({ length: 24 }).map((_, idx) => {
                    const seed = `${form.email || form.name || user?.uid || 'user'}-${idx+1}`;
                    const src = avatarUrl(seed, 96);
                    const selected = form.avatarSeed === seed;
                    return (
                      <button key={idx} type="button" onClick={()=>setForm(f=>({...f, avatarSeed: seed }))} className={`border rounded p-1 ${selected ? 'ring-2 ring-indigo-600' : 'hover:border-indigo-300'}`}>
                        <img alt="" src={src} className="h-16 w-16 rounded object-cover" />
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs text-gray-500 mt-2">A neutral placeholder silhouette is used until a photo is uploaded. Pick the background tone you prefer.</div>
              </div>
            )}
              <label className="block text-sm text-gray-700 mb-1">Photo URL</label>
              <input className="w-full border rounded p-2" value={form.photoUrl} onChange={(e)=>setForm(f=>({...f,photoUrl:e.target.value}))} placeholder="https://…" />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Avatar Source</label>
              <select className="w-full border rounded p-2" value={form.avatarUseGenerated ? 'generated' : 'photo'} onChange={(e)=>setForm(f=>({...f, avatarUseGenerated: e.target.value === 'generated'}))}>
                <option value="photo">Use uploaded/photo URL</option>
                <option value="generated">Use generated avatar</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Avatar Style</label>
              <select className="w-full border rounded p-2" value={form.avatarVariant} onChange={(e)=>setForm(f=>({...f, avatarVariant: e.target.value}))}>
                {AVATAR_VARIANTS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <div className="text-xs text-gray-500 mt-1">Preview updates instantly using your ID/email as seed.</div>
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              {!staffDoc && (
                <button disabled={saving} onClick={linkOrCreateStaff} className="px-3 py-2 border rounded">Create Staff Profile</button>
              )}
              <button disabled={saving} onClick={saveProfile} className="px-3 py-2 bg-indigo-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="font-semibold">Interventions I Lead</h2>
          </div>
          {(!staffDoc || leadInterventions.length === 0) ? (
            <div className="p-6 text-gray-500">{!staffDoc ? 'Create your staff profile to see interventions you lead.' : 'No interventions found.'}</div>
          ) : (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {leadInterventions.map(i => (
                <div key={i.id} className="border rounded p-4">
                  <div className="font-medium text-gray-900">{i.name}</div>
                  <div className="text-sm text-gray-600">{i.type || ''}</div>
                  <div className="text-xs text-gray-500 mt-1">{(i.students||[]).length} students • {i.status || 'active'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
